import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Table, Alert, Spinner, Badge, ProgressBar } from 'react-bootstrap';
import { Link, useParams, useLocation } from 'react-router-dom';
import reportService from '../../services/reportService';
import userService from '../../services/userService';
import projectService from '../../services/projectService';
import { formatHoursToHoursMinutes, formatDecimalHours, calculateTotalHours } from '../../utils/timeUtils';
import { FaClock, FaUser, FaCalendarAlt, FaTasks, FaChartBar, FaArrowLeft, FaFileAlt } from 'react-icons/fa';

const ProjectSummary = () => {
  const { projectId } = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const userId = queryParams.get('userId');

  const [project, setProject] = useState(null);
  const [user, setUser] = useState(null);
  const [reportsByDate, setReportsByDate] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalHours, setTotalHours] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Fetch project details
        const projectData = await projectService.getProjectById(projectId);
        setProject(projectData);

        // Fetch user details if userId is provided
        if (userId) {
          const userData = await userService.getUserById(userId);
          setUser(userData);
        }

        // Fetch all reports for this project
        const response = await reportService.getProjectSummary(projectId, userId);
        console.log('Project summary response:', response);
        
        // Extract reports from response (API returns {reports: [...], totalCount: number})
        const reportData = response || [];
        
        // Organize reports by date
        const reportsByDateMap = {};
        let totalHoursCount = 0;
        
        if (reportData && reportData.length > 0) {
          // Filter to only include reports with tasks for this project
          const relevantReports = reportData.filter(report => {
            // Keep if the report's main project matches
            if (report.project_id === parseInt(projectId)) return true;
            
            // Keep if any task has this project ID
            if (report.task_details && report.task_details.length > 0) {
              return report.task_details.some(task => task.project_id === parseInt(projectId));
            }
            
            return false;
          });
          
          console.log(`Filtered ${reportData.length} reports down to ${relevantReports.length} relevant for project ${projectId}`);
          
          relevantReports.forEach(report => {
            const date = new Date(report.date).toLocaleDateString();
            
            if (!reportsByDateMap[date]) {
              reportsByDateMap[date] = [];
            }
            
            reportsByDateMap[date].push(report);
            
            // Filter tasks to only include ones from current project
            const projectTasks = report.task_details && report.task_details.length > 0
              ? report.task_details.filter(task => task.project_id === parseInt(projectId))
              : [];
              
            // Calculate hours only from tasks for this specific project
            const projectHours = projectTasks.length > 0
              ? calculateTotalHours(projectTasks)
              : (report.project_id === parseInt(projectId) ? report.hours_worked : 0);
              
            totalHoursCount += parseFloat(projectHours || 0);
          });
        }
        console.log("reportsByDateMap",reportsByDateMap)
        setReportsByDate(reportsByDateMap);
        setTotalHours(totalHoursCount);
        
      } catch (err) {
        console.error('Error fetching project summary:', err);
        setError('Failed to load project summary: ' + (err.response?.data?.msg || err.message));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [projectId, userId]);

  if (loading) {
    return (
      
      <Container className="text-center mt-5">
        <Spinner animation="border" />
        <p>Loading project summary...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">{error}</Alert>
        <Button as={Link} to="/admin/reports" variant="secondary">
          Back to Reports
        </Button>
      </Container>
    );
  }

  return (
    <div className="page-fade-in">
    <Container fluid className="p-4">
      {/* Header Section */}
      <Card className="border-0 mb-4" style={{ borderRadius: 14, overflow: 'hidden' }}>
        <Card.Body style={{ 
          background: 'linear-gradient(135deg, #7C6FA0 0%, #9B8FBF 50%, #6BA3C7 100%)',
          color: '#fff', padding: '1.5rem'
        }}>
          <Row>
            <Col lg={8} md={12}>
              <div className="d-flex align-items-center mb-2">
                <FaFileAlt size={22} className="me-2" style={{ opacity: 0.9 }} />
                <h2 className="mb-0" style={{ fontWeight: 700, fontSize: '1.5rem' }}>
                  {project?.name || 'Loading...'}
                </h2>
              </div>
              {user && (
                <div className="d-flex align-items-center mt-2" style={{ opacity: 0.85 }}>
                  <FaUser className="me-2" />
                  <span style={{ fontSize: '1.05rem' }}>{user.name}</span>
                </div>
              )}
            </Col>
            <Col lg={4} md={12} className="d-flex flex-column justify-content-center align-items-end">
              <Button as={Link} to="/admin/reports" variant="light" className="d-flex align-items-center px-3 py-2" style={{ borderRadius: 50, fontWeight: 500, fontSize: '0.88rem' }}>
                <FaArrowLeft className="me-2" /> Back to Reports
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Stats Dashboard */}
      <Row className="mb-4">
        <Col lg={4} md={6} sm={12} className="mb-3">
          <Card className="h-100 border-0 shadow-sm hover-card">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h5 className="card-title text-primary fw-bold mb-0">Total Hours</h5>
                <FaClock className="text-primary" size={24} />
              </div>
              <h2 className="display-6 fw-bold">{formatHoursToHoursMinutes(totalHours)}</h2>
              <p className="text-muted">{formatDecimalHours(totalHours)} decimal hours</p>
              <ProgressBar 
                variant="success" 
                now={Math.min(totalHours * 10, 100)} 
                className="mt-2" 
                style={{height: '8px'}} 
              />
            </Card.Body>
          </Card>
        </Col>
        <Col lg={4} md={6} sm={12} className="mb-3">
          <Card className="h-100 border-0 shadow-sm hover-card">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h5 className="card-title text-primary fw-bold mb-0">Reports</h5>
                <FaFileAlt className="text-primary" size={24} />
              </div>
              <h2 className="display-6 fw-bold">{Object.values(reportsByDate).flat().length}</h2>
              <p className="text-muted">Across {Object.keys(reportsByDate).length} days</p>
              <ProgressBar 
                variant="info" 
                now={Math.min(Object.values(reportsByDate).flat().length * 5, 100)} 
                className="mt-2" 
                style={{height: '8px'}} 
              />
            </Card.Body>
          </Card>
        </Col>
        <Col lg={4} md={12} sm={12} className="mb-3">
          <Card className="h-100 border-0 shadow-sm hover-card">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h5 className="card-title text-primary fw-bold mb-0">Tasks</h5>
                <FaTasks className="text-primary" size={24} />
              </div>
              <h2 className="display-6 fw-bold">
                {Object.values(reportsByDate)
                  .flat()
                  .reduce((total, report) => {
                    const projectTasks = report.task_details?.filter(task => task.project_id === parseInt(projectId)) || [];
                    return total + projectTasks.length;
                  }, 0)}
              </h2>
              <p className="text-muted">Completed for this project</p>
              <ProgressBar 
                variant="warning" 
                now={Math.min(Object.values(reportsByDate).flat().length * 10, 100)} 
                className="mt-2" 
                style={{height: '8px'}} 
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {Object.keys(reportsByDate).length === 0 ? (
        <Alert variant="info" className="p-4 shadow-sm border-0">
          <div className="d-flex align-items-center">
            <FaCalendarAlt className="me-3 text-info" size={24} />
            <p className="mb-0 fs-5">No reports found for this project.</p>
          </div>
        </Alert>
      ) : (
        Object.keys(reportsByDate)
          .sort((a, b) => new Date(b) - new Date(a)) // Sort dates in descending order
          .map(date => (
            <Card key={date} className="mb-4 border-0 shadow-sm">
              <Card.Header className="bg-gradient bg-light py-3 px-4">
                <div className="d-flex align-items-center">
                  <FaCalendarAlt className="me-2 text-primary" />
                  <h5 className="mb-0 fw-bold">{date}</h5>
                  <Badge bg="primary" pill className="ms-3">
                    {reportsByDate[date].length} {reportsByDate[date].length === 1 ? 'Report' : 'Reports'}
                  </Badge>
                </div>
              </Card.Header>
              <Card.Body className="p-0">
                <Table hover responsive className="mb-0 table-striped">
                  <thead className="table-light">
                    <tr>
                      <th className="px-4 py-3"><FaUser className="me-2 text-secondary" /> User</th>
                      <th className="px-4 py-3"><FaTasks className="me-2 text-secondary" /> Tasks</th>
                      <th className="px-4 py-3"><FaClock className="me-2 text-secondary" /> Hours</th>
                      <th className="px-4 py-3"><FaChartBar className="me-2 text-secondary" /> Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportsByDate[date].map(report => (
                      <tr key={report.id}>
                        <td className="align-middle px-4 py-3">
                          <div className="d-flex align-items-center">
                            <div className="user-icon rounded-circle bg-light text-primary d-flex align-items-center justify-content-center me-2" 
                                 style={{width: '36px', height: '36px', fontSize: '18px'}}>
                              {report.user_name?.charAt(0).toUpperCase() || 'U'}
                            </div>
                            <div>
                              <p className="fw-bold mb-0">{report.user_name}</p>
                              <small className="text-muted">Report #{report.id}</small>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <p className="mb-1">{report.tasks}</p>
                          {report.task_details && report.task_details.length > 0 && (
                            <div className="mt-3">
                              <div className="d-flex align-items-center mb-2">
                                <div className="task-icon bg-primary text-white rounded p-1 me-2">
                                  <FaTasks size={14} />
                                </div>
                                <h6 className="fw-bold mb-0">Detailed Tasks</h6>
                              </div>
                              <ul className="mb-0 list-unstyled task-list">
                                {report.task_details
                                  .filter(task => task.project_id === parseInt(projectId))
                                  .map((task, idx) => (
                                    <li key={idx} className="py-2 ps-2 border-start border-primary mb-2" 
                                        style={{borderLeftWidth: '3px !important'}}>
                                      <div className="d-flex justify-content-between">
                                        <div>
                                          <p className="mb-0 task-description">{task.task_description}</p>
                                          <small className="text-primary">{task.project_name}</small>
                                        </div>
                                        <Badge bg="light" text="dark" className="hours-badge">
                                          {formatHoursToHoursMinutes(task.hours)}
                                        </Badge>
                                      </div>
                                    </li>
                                  ))}
                              </ul>
                            </div>
                          )}
                        </td>
                        <td className="align-middle px-4 py-3">
                          {(() => {
                            // Filter tasks to only include ones from current project
                            const projectTasks = report.task_details && report.task_details.length > 0
                              ? report.task_details.filter(task => task.project_id === parseInt(projectId))
                              : [];
                              
                            // Calculate hours only from tasks for this project
                            const projectHours = projectTasks.length > 0
                              ? calculateTotalHours(projectTasks)
                              : (report.project_id === parseInt(projectId) ? report.hours_worked : 0);
                              
                            const decimalHours = parseFloat(formatDecimalHours(projectHours));
                            const progressValue = Math.min(decimalHours * 12.5, 100);
                            
                            return (
                              <div>
                                <div className="d-flex align-items-center mb-2">
                                  <div className="hours-circle rounded-circle p-2 me-2 d-flex align-items-center justify-content-center"
                                       style={{
                                         width: '45px',
                                         height: '45px',
                                         backgroundColor: decimalHours > 6 ? '#198754' : decimalHours > 3 ? '#0d6efd' : '#6c757d',
                                         color: 'white'
                                       }}>
                                    <span className="fw-bold">{decimalHours}</span>
                                  </div>
                                  <div>
                                    <h5 className="mb-0 fw-bold">{formatHoursToHoursMinutes(projectHours)}</h5>
                                    <small className="text-muted">hours logged</small>
                                  </div>
                                </div>
                                <ProgressBar 
                                  now={progressValue} 
                                  variant={decimalHours > 6 ? "success" : decimalHours > 3 ? "primary" : "secondary"}
                                  style={{height: '5px'}}
                                />
                              </div>
                            );
                          })()}
                        </td>
                        <td className="align-middle px-4 py-3">
                          <Button
                            as={Link}
                            to={`/admin/reports/${report.id}`}
                            variant="outline-primary"
                            size="sm"
                            className="d-flex align-items-center px-3 py-2"
                          >
                            <FaFileAlt className="me-2" /> View Details
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          ))
      )}
    </Container>
    </div>
  );
};

export default ProjectSummary;
